import React, {Component} from "react"
import { CDN_PUB_URL } from "../../../../constants"
import SplitPanel from "../../../../components/SplitPanel"
import { MathKatex2 } from "../../../../components/MathKatex"
import AnswersGroup from "./AnswersGroup"
import Highlighter from "../../../../components/Highlighter"
import "./QuestionSAT.scss"
import { DirectionsForMathInQuestion } from "./Directions"

interface ISATQuestionProps {
    question: any
    highlighterEnabled: boolean
    onAnswer: (question, userAnswer: string) => void
    onFillInAnswer: (question, userAnswer: string) => void
    onBookmark: (bookmarked) => void
    onHighlight: (highlight) => void
}

interface ISATQuestinState {
    allowCrossOut: boolean
    question?: any
}

export default class SATQuestion extends Component<ISATQuestionProps, ISATQuestinState> {
    constructor(props) {
        super(props)
        this.state = {
            allowCrossOut: false
        }
    }

    componentDidMount(): void {
        this.setState({question: this.props.question})
    }

    componentDidUpdate(prevProps: Readonly<ISATQuestionProps>, prevState: Readonly<ISATQuestinState>, snapshot?: any): void {
        if (prevProps.question !== this.props.question) {
            this.setState({question: this.props.question})
        }
    }

    toggleCrossOut = () => {
        let {allowCrossOut} = this.state
        this.setState({allowCrossOut: !allowCrossOut})
    }

    handleBookmark = () => {
        let {question, onBookmark} = this.props
        onBookmark(question)
    }

    handleAnswer = (answer) => {
        let {question, onAnswer} = this.props
        onAnswer(question, answer)
    }

    handleFillInAnswer = (answer) => {
        let {question, onFillInAnswer} = this.props
        onFillInAnswer(question, answer)
    }

    handleCrossOut = (choice, crossed) => {
        let {question} = this.props
        if (!question.crossedOutInfo) {
            question.crossedOutInfo = {}
        }
        question.crossedOutInfo[choice] = crossed
    }

    handleHighlightLeft = (highlightedHTML) => {
        let {question, onHighlight} = this.props
        if (highlightedHTML !== "") {
            question.highlightedLeftHTML = highlightedHTML
        }
        else {
            question.highlightedLeftHTML = undefined
        }
        this.setState({question})
        onHighlight(question)
    }

     handleHighlightRight = (highlightedHTML) => {
        let {question, onHighlight} = this.props
        if (highlightedHTML !== "") {
            question.highlightedRightHTML = highlightedHTML
        }
        else {
            question.highlightedRightHTML = undefined
        }
        this.setState({question})
        onHighlight(question)
    }

    // Per Jennifer, choices may have images as well
    prepareChoice = (choice: string) => {
        if (!choice) return ""
        if (choice.includes("$CDN")) {
            let url = choice.replace("$CDN", CDN_PUB_URL)
            return `<img src=${url} class="question-option" alt="option"/>`
        }
        return choice
    }
 
    render() {
        let {question} = this.state

        if (!question) return null

        let {layoutColumns=1, questionImage="", crossedOutInfo, acceptableAnswers, questionNumber, questionText, questionQualifier, choiceA, choiceB, choiceC, choiceD} = question
        let {highlighterEnabled} = this.props

        questionImage = questionImage && questionImage.replace("$CDN", CDN_PUB_URL)

        let infoLeft = questionImage ? 
            `<img src="${questionImage}" alt="question ${questionNumber}" class="question-image" /> <br/> ${questionText}`
            :
            questionText

        let infoRight = acceptableAnswers ? `${infoLeft} ${questionQualifier}` : questionQualifier

        // If we have highlighted HTML then use it!
        infoLeft = question.highlightedLeftHTML ? question.highlightedLeftHTML : infoLeft
        infoRight = question.highlightedRightHTML ? question.highlightedRightHTML : infoRight

        let {allowCrossOut} = this.state
        let {userSubmittedAnswer, fillInAnswer=""} = question
        let options = [
            { value: "A", label: this.prepareChoice(choiceA), },
            { value: "B", label: this.prepareChoice(choiceB), },
            { value: "C", label: this.prepareChoice(choiceC), },
            { value: "D", label: this.prepareChoice(choiceD), },
        ]

        return (
            <div className="sat-question-container">
                {layoutColumns === 2 ?
                    <SplitPanel classNameLeftPanel="split-panel-left" classNameRightPanel="split-panel-right"
                        panelLeft={
                            <div className="split-panel--inner">
                                {!acceptableAnswers ?
                                    <Highlighter enabled={highlighterEnabled} onHighlight={this.handleHighlightLeft}>
                                        <MathKatex2 mixedContent={infoLeft} />
                                    </Highlighter>
                                    :
                                    <DirectionsForMathInQuestion />
                                }
                            </div>
                        }
                        panelRight={
                            <div className="split-panel--inner">
                                <QuestionInfo question={question} onBookmark={this.handleBookmark} onAllowCrossOut={this.toggleCrossOut}/>
                                <Highlighter enabled={highlighterEnabled} onHighlight={this.handleHighlightRight}>
                                    <MathKatex2 mixedContent={infoRight} />
                                </Highlighter>
                                <AnswersGroup
                                    questionNumber={questionNumber}
                                    options={options}
                                    acceptableAnswers={acceptableAnswers}
                                    selectedAnswer={userSubmittedAnswer}
                                    fillInAnswer={fillInAnswer}
                                    allowCrossOut={allowCrossOut}
                                    crossedOutInfo={crossedOutInfo}
                                    onSelect={this.handleAnswer}
                                    onFillInAnswer={this.handleFillInAnswer}
                                    onCrossOut={this.handleCrossOut}
                                />
                            </div>
                        }
                        allowResize={true}
                    />
                    :
                    <SinglePanel className="single-panel-container">
                        <QuestionInfo question={question} onBookmark={this.handleBookmark} onAllowCrossOut={this.toggleCrossOut}/>
                        <MathKatex2 mixedContent={infoLeft} />
                        <MathKatex2 mixedContent={infoRight} />
                        <AnswersGroup
                            questionNumber={questionNumber}
                            options={options}
                            acceptableAnswers={acceptableAnswers}
                            selectedAnswer={userSubmittedAnswer}
                            fillInAnswer={fillInAnswer}
                            allowCrossOut={allowCrossOut}
                            crossedOutInfo={crossedOutInfo}
                            onSelect={this.handleAnswer}
                            onFillInAnswer={this.handleFillInAnswer}
                            onCrossOut={this.handleCrossOut}
                        />
                    </SinglePanel>
                }
            </div>
        )
    }
}

//--- Helpers ---

function QuestionInfo(props) {
    let {question, onBookmark, onAllowCrossOut} = props
    let {questionNumber} = question

    return (
        <div className="question-info-bar mb-3">
            <div className="question-number-area">{questionNumber}</div>
            <div className="bookmark-area"><Bookmark question={question} onBookmark={onBookmark}/></div>
            <div className="cross-out-area">
                <button aria-label="Cross out answer choices you think are wrong." onClick={onAllowCrossOut}>
                <svg width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M29.62 0H2.88C1.29 0 0 1.29 0 2.88V29.62C0 31.21 1.29 32.5 2.88 32.5H29.62C31.21 32.5 32.5 31.21 32.5 29.62V2.88C32.5 1.29 31.21 0 29.62 0ZM2.88 1.81H29.43L19.43 11.81C18.91 11.41 18.19 11.21 17.26 11.2H12.39V18.85L12.24 19L9.48 11.2H7.11L3.53 21.23H5.72L6.41 19.17H10.11L10.58 20.66L1.81 29.43V2.88C1.81 2.29 2.29 1.81 2.88 1.81ZM14.44 16.81L14.4 16.85V16.81H14.44ZM14.4 15.15V12.94H16.61C17.1 12.94 17.49 12.99 17.81 13.1C17.89 13.13 17.97 13.18 18.03 13.23L16.11 15.15H14.41H14.4ZM9.54 17.43H6.99L8.28 13.48L9.53 17.43H9.54ZM29.62 30.69H3.11L12.58 21.22L14.39 19.41L16.98 16.81L20.31 13.48L21.04 12.75C21.04 12.75 21.02 12.77 21.01 12.79L22.11 11.69C22.11 11.69 22.08 11.72 22.06 11.73L30.69 3.11V29.63C30.69 30.22 30.21 30.7 29.62 30.7V30.69ZM22.85 13.71C22.41 14.33 22.19 15.19 22.19 16.32C22.19 17.45 22.42 18.29 22.89 18.85C23.36 19.41 23.95 19.69 24.66 19.69C25.37 19.69 25.96 19.45 26.34 18.97C26.55 18.71 26.73 18.32 26.87 17.81H28.95C28.77 18.9 28.31 19.79 27.56 20.48C26.81 21.17 25.85 21.51 24.69 21.51C23.25 21.51 22.11 21.05 21.29 20.12C20.99 19.79 20.77 19.39 20.58 18.97C20.51 19.26 20.4 19.54 20.23 19.8C20.04 20.11 19.8 20.38 19.52 20.59C19.2 20.84 18.82 21 18.38 21.09C17.94 21.18 17.47 21.23 16.95 21.23H15.15L16.89 19.49C17.32 19.49 17.65 19.43 17.89 19.32C18.33 19.1 18.56 18.68 18.56 18.07C18.56 17.99 18.55 17.92 18.54 17.85L19.97 16.42C19.97 16.42 20.03 16.47 20.06 16.5C20.06 16.45 20.06 16.4 20.05 16.34L25.31 11.08C26.62 11.23 27.62 11.74 28.28 12.68C28.71 13.3 28.94 13.93 28.97 14.55H26.88C26.74 14.07 26.57 13.71 26.35 13.46C25.97 13.02 25.4 12.81 24.66 12.81C23.92 12.81 23.3 13.12 22.86 13.73L22.85 13.71Z" fill="black"/>
                </svg>
                <span className="sr-only">ABC</span>
                </button>
            </div>
        </div>
    )
}

function AnswerBox(props) {
    return (
        <div>
            Answer here
            <input type="text" />
        </div>
    )
}

function SinglePanel(props) {
    let {className="", children} = props

    return (
        <div className={className}>
            {children}
        </div>
    )
}

function Bookmark(props) {
    let {question, onBookmark} = props
    let {bookmarked} = question
    let imgUrl = `/assets/images/icons/v2/${!bookmarked ? "ico-bookmark.svg" : "ico-bookmark-bright.svg"}`

    return (
        <button className="bookmark-btn" onClick={onBookmark}>
            <span className="bookmark-icon">
                <img src={imgUrl} width="14" height="19" alt="bookmark"/>
            </span>
            <span className={`bookmark-label ${bookmarked ? "bookmarked": ""}`}>
                Mark for Review
            </span>
        </button>
    )
}
